import * as React from "react";
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router";
import { GitMerge, Loader2, CheckCheck, FolderTree, Info, ListChecks, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Progress } from "@/components/ui/progress";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { colorVariantClasses } from "@/theme/AppColorVariant";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { invalidateConflictDecisionQueries } from "../../lib/conflictQueries";
import { conflictRulePagePath } from "../../models/ConflictRule";
import TypedCountConfirmation, {
  needsTypedConfirmation as needsTypedCount,
  TYPED_CONFIRMATION_THRESHOLD,
  typedCountMatches as matchesTypedCount,
} from "../common/TypedCountConfirmation";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import {
  ConflictResolutionAction,
  ConflictResolutionActionColors,
  ConflictResolutionActionDescriptions,
  ConflictResolutionActionLabels,
  ConflictResolutionOutcomeLabels,
  ConflictResolutionOutcomeShortLabels,
  transfersFileContent,
} from "../../models/ConflictResolutionAction";
import {
  ConflictReason,
  ConflictReasonColors,
  ConflictReasonDescriptions,
  ConflictReasonIcons,
  ConflictReasonLabels,
  isContentMismatch,
} from "../../models/ConflictReason";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import {
  CONFLICT_FILE_FAMILIES,
  OrgConflictReasonSummary,
} from "../../models/OrgConflictResolution";
import { ConflictLegend } from "@/components/common/Table/File/ConflictLegend";
import { ActionSelectItem } from "@/components/common/Table/File/ActionSelectItem";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface OrgConflictResolveViewProps {
  /** The organization being viewed. Scopes the conflict query so switching orgs never shows another org's stale conflicts. */
  organizationId: string;
  disabled?: boolean;
}

/**
 * Sentinel for "don't touch this reason". Not a member of ConflictResolutionAction: it maps to OMITTING the
 * reason from the request, so the server can never action a reason the operator left alone.
 */
const LEAVE_UNRESOLVED = "leaveUnresolved";

type ReasonChoice = ConflictResolutionAction | typeof LEAVE_UNRESOLVED;

/**
 * Submission runs in two visible stages. The statistics stage is not cosmetic: the conflict counts this page and
 * the entity lists read are a recalculated projection of the files, so they are stale the moment the resolve
 * commits and stay stale until a recalculation pass runs.
 */
type SubmitPhase = "idle" | "resolving" | "refreshingStatistics";

/** Reason order is fixed rather than data-driven, so the rows don't reshuffle as counts change. */
const REASON_ORDER: ConflictReason[] = [
  ConflictReason.SizeMismatch,
  ConflictReason.HashMismatch,
  ConflictReason.PathCollision,
  ConflictReason.PathCollisionDuplicate,
];

/**
 * The actions offered org-wide for a reason, mirroring the server's ConflictResolutionPolicy.IsBulkActionAllowed.
 * A content mismatch permits all four: it is raised only when a SharePoint item already holds the target path, so
 * there is provably something to overwrite or adopt. A path collision permits only the two actions that adopt
 * nothing — several files contend for one path, so a blanket replace would let the last writer win
 * nondeterministically and a blanket accept would adopt an occupant that may not exist. Those need a winner chosen
 * per entity in the entity's own drawer. The server enforces this too, so the dropdown is a convenience, not the
 * control.
 */
const actionsFor = (reason: ConflictReason): ConflictResolutionAction[] =>
  isContentMismatch(reason)
    ? [
        ConflictResolutionAction.ReplaceFromDisk,
        ConflictResolutionAction.AcceptSharePoint,
        ConflictResolutionAction.RenameKeepBoth,
        ConflictResolutionAction.SkipAcknowledge,
      ]
    : [ConflictResolutionAction.RenameKeepBoth, ConflictResolutionAction.SkipAcknowledge];

const formatSize = (megabytes: number): string =>
  megabytes >= 1024 ? `${(megabytes / 1024).toFixed(2)} GB` : `${megabytes.toFixed(2)} MB`;

const plural = (count: number, singular: string, pluralForm = `${singular}s`): string =>
  `${count.toLocaleString()} ${count === 1 ? singular : pluralForm}`;

/**
 * Org-wide (cross-entity) conflict resolution. Every reason starts at "Leave unresolved", so any subset can be
 * actioned independently — an operator can clear the content mismatches without also blanket-actioning path
 * collisions that genuinely need a winner chosen per entity.
 *
 * The whole operation is one request: the server resolves each selected reason as a scoped set, bounded by the
 * pre-flight snapshot, and writes a single audit entry. Files come back QUEUED for re-upload — the SharePoint
 * side only changes once the migration queue reaches them.
 */
export const OrgConflictResolveView: React.FC<OrgConflictResolveViewProps> = ({ organizationId, disabled }) => {
  const organizationsRepo = new OrganizationsRepository();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  // Aborting the request is what stops the run: the server stops after the batch in progress and keeps what it
  // committed.
  const runAbort = useRef<AbortController | null>(null);
  const [families, setFamilies] = useState<MigrationType[]>([]);
  const [reasonChoices, setReasonChoices] = useState<Record<ConflictReason, ReasonChoice>>(
    () =>
      REASON_ORDER.reduce(
        (acc, reason) => ({ ...acc, [reason]: LEAVE_UNRESOLVED }),
        {} as Record<ConflictReason, ReasonChoice>
      )
  );
  const [confirming, setConfirming] = useState(false);
  const [typedCount, setTypedCount] = useState("");
  const [phase, setPhase] = useState<SubmitPhase>("idle");
  /** Files the server has confirmed committed, from the resolve's progress stream. Not an estimate. */
  const [resolvedFiles, setResolvedFiles] = useState(0);
  /** The confirmed file count of the run in flight — see targetFiles for why this cannot be read live. */
  const [runTargetFiles, setRunTargetFiles] = useState(0);

  /** Both stages keep the panel open and the controls locked, so everything that gated on "in flight" still does. */
  const submitting = phase !== "idle";

  const selectedReasons = useMemo(
    () => REASON_ORDER.filter((reason) => reasonChoices[reason] !== LEAVE_UNRESOLVED),
    [reasonChoices]
  );

  // Keyed on the sorted selection so flipping choices back and forth is served from cache instead of refetched.
  const preflightQuery = useQuery({
    queryKey: [
      QUERY_KEYS.ORG_CONFLICT_PREFLIGHT,
      organizationId,
      [...selectedReasons].sort().join(","),
      [...families].sort().join(","),
    ],
    enabled: open && !disabled,
    queryFn: () => organizationsRepo.getConflictResolutionPreflight(selectedReasons, families),
    // The pre-flight counts conflicts that are still UNRESOLVED, so re-running it during a resolve returns the
    // shrinking remainder rather than the set the run is working through. The default focus refetch therefore fired
    // a six-figure aggregate mid-run for a number nothing can act on until the run ends. The dialog is kept fresh
    // by mounting, by the selection re-keying this query, and by the explicit invalidation after a run.
    refetchOnWindowFocus: false,
  });

  const preflight = preflightQuery.data;

  /** Per-reason totals summed across the scoped families — what the operator acts on and what the server checks. */
  const reasonTotals = useMemo(() => {
    const totals = new Map<ConflictReason, OrgConflictReasonSummary>();
    for (const row of preflight?.reasons ?? []) {
      const existing = totals.get(row.conflictReason);
      totals.set(row.conflictReason, {
        family: row.family,
        conflictReason: row.conflictReason,
        fileCount: (existing?.fileCount ?? 0) + row.fileCount,
        // Entity counts are NOT summed across reasons anywhere it matters — the aggregate figures below come from
        // the server's distinct counts. Within one reason, families are disjoint, so this sum is exact.
        entityCount: (existing?.entityCount ?? 0) + row.entityCount,
        totalMegabytes: (existing?.totalMegabytes ?? 0) + row.totalMegabytes,
        officeFileCount: (existing?.officeFileCount ?? 0) + row.officeFileCount,
      });
    }
    return totals;
  }, [preflight]);

  const presentReasons = REASON_ORDER.filter((reason) => (reasonTotals.get(reason)?.fileCount ?? 0) > 0);
  const nothingToResolve = !preflightQuery.isLoading && !preflightQuery.isError && presentReasons.length === 0;

  const selected = preflight?.selected;
  const residual = preflight?.residual;
  const officeFilesInSelection = selected?.officeFileCount ?? 0;
  const replacingFromDisk = selectedReasons.some(
    (reason) => reasonChoices[reason] === ConflictResolutionAction.ReplaceFromDisk
  );

  // Only the reasons whose chosen action actually pushes content. Accepting the SharePoint copy or acknowledging
  // resolves the conflict without transferring anything, so their size must not be presented as upload volume.
  const transferMegabytes = selectedReasons
    .filter((reason) => transfersFileContent(reasonChoices[reason] as ConflictResolutionAction))
    .reduce((sum, reason) => sum + (reasonTotals.get(reason)?.totalMegabytes ?? 0), 0);

  const needsTypedConfirmation = needsTypedCount(selected?.fileCount ?? 0);
  const typedCountMatches = matchesTypedCount(typedCount, selected?.fileCount ?? 0);
  const canSubmit =
    !disabled &&
    !submitting &&
    selectedReasons.length > 0 &&
    (selected?.fileCount ?? 0) > 0 &&
    !!preflight?.preflightTakenAt;

  const selectionLabel = selectedReasons.map((reason) => ConflictReasonLabels[reason]).join(" + ");

  const chunkSize = preflight?.chunkSize ?? TYPED_CONFIRMATION_THRESHOLD;

  /**
   * Denominator of the progress readout, frozen for the duration of a run.
   *
   * It must be what the operator confirmed, not live query data. The pre-flight counts conflicts that are still
   * unresolved, so any refetch during a resolve returns the shrinking remainder: reading it live let the
   * denominator collapse under the run and produced "83,300 of 30,000 files" with the bar pinned at 100% and the
   * batch counter stuck on its last value. The count of files the run is resolving cannot change once it starts —
   * the snapshot bound and the confirmed per-reason counts are what guarantee that server-side.
   */
  const targetFiles = submitting ? runTargetFiles : selected?.fileCount ?? 0;
  const totalChunks = Math.max(1, Math.ceil(targetFiles / chunkSize));

  const progressPercent = targetFiles > 0 ? Math.min(100, (resolvedFiles / targetFiles) * 100) : 0;
  // The batch in flight is the one after everything committed, so it leads the committed count by one.
  const currentBatch = Math.min(totalChunks, Math.floor(resolvedFiles / chunkSize) + 1);

  const resetChoices = () => {
    setReasonChoices(
      REASON_ORDER.reduce(
        (acc, reason) => ({ ...acc, [reason]: LEAVE_UNRESOLVED }),
        {} as Record<ConflictReason, ReasonChoice>
      )
    );
    setConfirming(false);
    setTypedCount("");
  };

  /**
   * Makes the page tell the truth again once files have moved, whether the run finished or stopped part-way.
   *
   * Every resolved file only flags its parent entity's statistics row dirty; the conflict counts this page and the
   * entity lists read are recomputed by a recalculation pass. Invalidating first would refetch the pre-resolve
   * numbers and cache them as fresh, leaving the page confidently wrong until a background pass and some later
   * refetch happened to coincide. So: recalculate, then invalidate.
   */
  const refreshAfterResolve = async ({ clearSelection }: { clearSelection: boolean }) => {
    setPhase("refreshingStatistics");
    try {
      await organizationsRepo.recalculateDirtyOrganizationStatistics(organizationId);
    } catch {
      toast.warning("Conflicts resolved, but their statistics could not be refreshed now.", {
        description: "The counts on this page catch up on the next scheduled recalculation.",
        duration: 10000,
      });
    }

    await invalidateConflictDecisionQueries(queryClient);

    // Clearing the selection re-keys the pre-flight query, so it happens after the invalidation rather than during
    // the run — earlier would swap the panel to a different cache entry mid-flight and take the readout with it.
    if (clearSelection) resetChoices();
    await preflightQuery.refetch();
  };

  const handleResolve = async () => {
    if (!preflight) return;

    const reasonActions: Partial<Record<ConflictReason, ConflictResolutionAction>> = {};
    const expectedFileCounts: Partial<Record<ConflictReason, number>> = {};
    for (const reason of selectedReasons) {
      reasonActions[reason] = reasonChoices[reason] as ConflictResolutionAction;
      expectedFileCounts[reason] = reasonTotals.get(reason)?.fileCount ?? 0;
    }

    // Tracked outside React state as well, because the failure path has to know whether anything committed and a
    // state value read after an await is the one captured at render.
    let committedFiles = 0;

    setPhase("resolving");
    setResolvedFiles(0);
    // Captured here, from the same pre-flight figures the operator just confirmed and the request carries as its
    // expected counts, so the readout is measured against the set the server is actually resolving.
    setRunTargetFiles(
      selectedReasons.reduce((sum, reason) => sum + (reasonTotals.get(reason)?.fileCount ?? 0), 0)
    );
    runAbort.current = new AbortController();
    try {
      const result = await organizationsRepo.resolveConflictsOrgWide(
        {
          reasonActions,
          families: families.length > 0 ? families : undefined,
          preflightTakenAt: preflight.preflightTakenAt,
          expectedFileCounts,
        },
        (progress) => {
          committedFiles = progress.resolvedFileCount;
          setResolvedFiles(progress.resolvedFileCount);
        },
        runAbort.current.signal
      );

      // Described per action, because "resolved" means four different things. Only a replace or a rename queues
      // anything for transfer; accepting adopts the SharePoint copy and uploads nothing, and acknowledging leaves
      // the file excluded from upload entirely. One flattened "queued for re-upload" line would be wrong for half
      // of them, and the byte figure only covers the two that actually move content.
      const byAction = new Map<ConflictResolutionAction, number>();
      for (const outcome of result.outcomes)
        byAction.set(
          outcome.action,
          (byAction.get(outcome.action) ?? 0) + outcome.resolvedFileCount
        );

      const lines = [...byAction.entries()]
        .filter(([, count]) => count > 0)
        .map(([action, count]) => {
          const suffix =
            transfersFileContent(action) && result.totalQueuedMegabytes > 0 && byAction.size === 1
              ? ` (${formatSize(result.totalQueuedMegabytes)})`
              : "";
          return `${plural(count, "file")} ${ConflictResolutionOutcomeLabels[action]}${suffix}`;
        });

      const transferring = result.outcomes
        .filter((outcome) => transfersFileContent(outcome.action))
        .reduce((sum, outcome) => sum + outcome.resolvedFileCount, 0);
      if (byAction.size > 1 && transferring > 0)
        lines.push(`${formatSize(result.totalQueuedMegabytes)} queued for transfer to SharePoint`);

      const residualFiles = result.leftUnresolved.reduce((sum, row) => sum + row.fileCount, 0);
      if (residualFiles > 0)
        lines.push(`${plural(residualFiles, "file")} left unresolved for per-entity review`);

      // Reported before the statistics stage, which can take a while on a large organization. What was resolved is
      // already committed and is what the operator asked about; making them wait for a refresh to hear it would be
      // backwards.
      toast.success(`${plural(result.totalResolvedFileCount, "conflict")} resolved`, {
        description: lines.join("\n"),
        duration: 10000,
      });

      await refreshAfterResolve({ clearSelection: true });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        toast.info(`Stopped. ${plural(committedFiles, "conflict")} resolved before the stop stay resolved.`, {
          duration: 10000,
        });
      } else {
        toast.error(error instanceof Error ? error.message : "Failed to resolve conflicts", {
          duration: 10000,
        });
      }

      // Batches that committed before the failure are permanent, so the page is exactly as stale as after a run
      // that finished. The selection is deliberately kept: re-running resolves the remainder, and the refreshed
      // pre-flight is what supplies the counts that re-run has to confirm.
      if (committedFiles > 0) await refreshAfterResolve({ clearSelection: false });
    } finally {
      runAbort.current = null;
      setPhase("idle");
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Button
        variant="outline"
        size="sm"
        className="gap-2 text-amber-600 dark:text-amber-400"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <GitMerge className="size-4" />
        Resolve conflicts (org-wide)
      </Button>

      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) runAbort.current?.abort();
          setOpen(next);
          if (!next) resetChoices();
        }}
      >
        <SheetContent side="right" className="sm:max-w-xl w-full gap-0">
          <SheetHeader className="border-b">
            <SheetTitle className="flex items-center gap-2">
              <GitMerge className="size-5 text-amber-600 dark:text-amber-400" />
              Resolve conflicts org-wide
              {/* Clear of the sheet's own close button, which sits in the same corner. */}
              <span className="ml-auto mr-8 flex items-center gap-1">
                <DocsHelpLink to="how-to/resolve-conflicts" label="How to resolve conflicts" />
                <ConflictLegend />
              </span>
            </SheetTitle>
            <SheetDescription>
              Every reason starts at <span className="font-medium">Leave unresolved</span>. Choose an action only
              for the reasons you want to act on — the rest are left completely untouched.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4">
            {preflightQuery.isLoading && (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Counting unresolved conflicts…
              </div>
            )}

            {preflightQuery.isError && (
              <div className="flex flex-col items-center justify-center gap-3 py-12">
                <p className="text-sm text-muted-foreground">Failed to load conflict counts.</p>
                <Button variant="outline" size="sm" onClick={() => preflightQuery.refetch()}>
                  Retry
                </Button>
              </div>
            )}

            {nothingToResolve && (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <FolderTree className="size-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Nothing has unresolved conflicts.</p>
              </div>
            )}

            {presentReasons.length > 0 && (
              <>
                <div className="flex flex-col gap-2">
                  <span className="text-xs text-muted-foreground">Limit to (optional)</span>
                  <ToggleGroup
                    type="multiple"
                    variant="outline"
                    size="sm"
                    value={families}
                    onValueChange={(next) => setFamilies(next as MigrationType[])}
                    disabled={disabled || submitting}
                    className="flex-wrap justify-start"
                  >
                    {CONFLICT_FILE_FAMILIES.map((family) => (
                      <ToggleGroupItem key={family} value={family}>
                        {MigrationTypeLabels[family]}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  <span className="text-xs text-muted-foreground">
                    {families.length === 0
                      ? "All entity types included."
                      : `Only ${families.map((f) => MigrationTypeLabels[f]).join(", ")}.`}
                  </span>
                </div>

                <div
                  className={cn(
                    "flex flex-col gap-2 transition-opacity",
                    submitting && "pointer-events-none opacity-50"
                  )}
                >
                  <span className="text-xs text-muted-foreground">Resolve each conflict reason as</span>
                  {presentReasons.map((reason) => {
                    const ReasonIcon = ConflictReasonIcons[reason];
                    const totals = reasonTotals.get(reason);
                    return (
                      <div key={reason} className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex cursor-help">
                                  <Badge variant={ConflictReasonColors[reason]} className="gap-1">
                                    {ReasonIcon && <ReasonIcon className="size-3" />}
                                    {ConflictReasonLabels[reason]}
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                {ConflictReasonDescriptions[reason]}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {plural(totals?.fileCount ?? 0, "file")} ·{" "}
                            {plural(totals?.entityCount ?? 0, "entity", "entities")} ·{" "}
                            {formatSize(totals?.totalMegabytes ?? 0)}
                          </span>
                        </div>
                        <Select
                          value={reasonChoices[reason]}
                          onValueChange={(next) => {
                            setReasonChoices((prev) => ({ ...prev, [reason]: next as ReasonChoice }));
                            setConfirming(false);
                            setTypedCount("");
                          }}
                          disabled={disabled || submitting}
                        >
                          <SelectTrigger size="sm" className="w-52 shrink-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <ActionSelectItem
                              value={LEAVE_UNRESOLVED}
                              label="Leave unresolved"
                              description="Change nothing for this reason."
                              labelClassName="text-muted-foreground"
                            />
                            {actionsFor(reason).map((action) => (
                              <ActionSelectItem
                                key={action}
                                value={action}
                                label={ConflictResolutionActionLabels[action]}
                                description={ConflictResolutionActionDescriptions[action]}
                                labelClassName={cn(
                                  colorVariantClasses[ConflictResolutionActionColors[action]]?.text
                                )}
                              />
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>

                {selectedReasons.length === 0 && (
                  <div className="flex items-start gap-2 rounded-md border border-sky-500/30 bg-sky-500/5 p-3 text-xs text-muted-foreground">
                    <Info className="mt-0.5 size-3.5 shrink-0 text-sky-600 dark:text-sky-400" />
                    <span>
                      Choose an action for at least one reason. “Replace from disk” is available for Size and Hash
                      Mismatch, where the SharePoint copy already occupies the target path. Path collisions need a
                      winner nominated per entity, so resolve those from an entity’s
                      <span className="font-medium"> Resolve conflicts </span>
                      drawer, or pick the winners of many groups at once with{" "}
                      <Link
                        to={conflictRulePagePath(organizationId)}
                        className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                      >
                        <ListChecks className="size-3.5" />
                        Resolve Conflicts by Rule
                      </Link>
                      .
                    </span>
                  </div>
                )}

                {selectedReasons.length > 0 && selected && residual && (
                  <div className="flex flex-col gap-2 rounded-md border bg-card p-4">
                    <p className="text-sm">
                      Resolving <span className="font-semibold">{plural(selected.fileCount, "file")}</span> across{" "}
                      <span className="font-semibold">{plural(selected.entityCount, "entity", "entities")}</span>
                      {" — "}
                      {selectionLabel}
                    </p>
                    <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                      {selectedReasons.map((reason) => {
                        const action = reasonChoices[reason] as ConflictResolutionAction;
                        const totals = reasonTotals.get(reason);
                        return (
                          <li key={reason}>
                            <span className="font-medium text-foreground">
                              {plural(totals?.fileCount ?? 0, "file")}
                            </span>{" "}
                            ({ConflictReasonLabels[reason]}) {ConflictResolutionOutcomeShortLabels[action]}.
                          </li>
                        );
                      })}
                    </ul>
                    {transferMegabytes > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {formatSize(transferMegabytes)} will be queued for transfer to SharePoint.
                      </p>
                    )}
                    {residual.fileCount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium">{plural(residual.fileCount, "file")}</span> (
                        {REASON_ORDER.filter(
                          (reason) =>
                            reasonChoices[reason] === LEAVE_UNRESOLVED &&
                            (reasonTotals.get(reason)?.fileCount ?? 0) > 0
                        )
                          .map((reason) => ConflictReasonLabels[reason])
                          .join(" + ")}
                        ) will be left unresolved for per-entity review.
                      </p>
                    )}
                  </div>
                )}

                {selectedReasons.length > 0 && replacingFromDisk && (
                  <div className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
                    <div className="flex items-start gap-2">
                      <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
                      <div className="flex flex-col gap-1.5">
                        <span className="font-medium">“Replace from disk” overwrites the SharePoint copy.</span>
                        {officeFilesInSelection > 0 ? (
                          <span className="text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {plural(officeFilesInSelection, "Office file")}
                            </span>{" "}
                            in this selection could be overwritten with a SharePoint-side edit lost. Recovery
                            depends on version history being enabled on the target library — confirm that before
                            proceeding. Every other format in the selection is never co-authored in SharePoint, so
                            the local file is authoritative.
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            No Office-format files are in this selection, so no co-authored document can be
                            clobbered. Recovery still depends on SharePoint version history.
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          Content-mismatch detection is first-upload-only: after a replace, these files will not
                          raise Size or Hash conflicts again — later disk edits overwrite SharePoint silently.
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {submitting && selected && (
                  <div className="flex flex-col gap-2 rounded-md border border-primary/40 bg-primary/5 p-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
                      <span className="font-medium">
                        {phase === "resolving" ? "Resolving conflicts…" : "Refreshing statistics…"}
                      </span>
                      {phase === "resolving" && (
                        <span className="ml-auto font-mono text-xs text-muted-foreground">
                          {Math.round(progressPercent)}%
                        </span>
                      )}
                    </div>

                    <Progress value={phase === "resolving" ? progressPercent : 100} className="h-2" />

                    {phase === "resolving" ? (
                      <>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span>
                            {resolvedFiles.toLocaleString()} of {targetFiles.toLocaleString()} files
                          </span>
                          <span className="text-foreground/70">
                            batch {currentBatch.toLocaleString()} of {totalChunks.toLocaleString()}
                          </span>
                          <span className="ml-auto italic">do not close this panel</span>
                        </div>

                        <p className="text-xs text-muted-foreground">
                          Each batch of {chunkSize.toLocaleString()} is committed as it completes and reported here,
                          so the count is work already saved — closing this panel stops the run but keeps it.
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Resolved. Refreshing the conflict counts this page reads.
                      </p>
                    )}
                  </div>
                )}

                {confirming && !submitting && selected && (
                  <div className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
                    <span className="text-xs">
                      Apply {selectionLabel} to {plural(selected.fileCount, "file")}?
                    </span>
                    {needsTypedConfirmation && (
                      <TypedCountConfirmation
                        fileCount={selected.fileCount}
                        value={typedCount}
                        onChange={setTypedCount}
                      />
                    )}
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setConfirming(false);
                          setTypedCount("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleResolve}
                        disabled={needsTypedConfirmation && !typedCountMatches}
                      >
                        Confirm
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <SheetFooter className="border-t">
            {selectedReasons.length === 0 ? (
              <Button className="w-full gap-2" disabled>
                <CheckCheck className="size-4" />
                Select at least one conflict reason
              </Button>
            ) : (
              <Button
                className="w-full gap-2"
                disabled={!canSubmit || confirming}
                onClick={() => setConfirming(true)}
                title={`Resolve ${plural(selected?.fileCount ?? 0, "file")} across ${plural(
                  selected?.entityCount ?? 0,
                  "entity",
                  "entities"
                )} (${selectionLabel})`}
              >
                <CheckCheck className="size-4 shrink-0" />
                {/* The counts are what the operator is confirming, so they never shrink. The reason list is
                    the part allowed to ellipsis: with all four reasons selected it is far too long for the
                    button, and Button's default whitespace-nowrap would otherwise spill it past the edge. */}
                <span className="shrink-0">
                  Resolve {plural(selected?.fileCount ?? 0, "file")} across{" "}
                  {plural(selected?.entityCount ?? 0, "entity", "entities")}
                </span>
                <span className="min-w-0 truncate">({selectionLabel})</span>
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
};

export default OrgConflictResolveView;
